import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiFailure,
  type Aggregation,
  type Anomaly,
  type CalendarPeriod,
  type Column,
  type Dataset,
  type ForecastModelName,
  type ModelComparison,
  type Projection,
} from '../api';
import type { VizColors } from '../theme';
import { BacktestChart, ComponentsChart, ForecastChart, ResidualChart } from './Charts';
import { Notice } from './Panels';

const PERIODS: CalendarPeriod[] = ['day', 'week', 'month', 'quarter', 'year'];
const MODELS: { value: ForecastModelName | 'auto'; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'decomposition', label: 'Trend + seasonality' },
  { value: 'seasonal-naive', label: 'Seasonal naive' },
  { value: 'linear', label: 'Linear' },
  { value: 'drift', label: 'Drift' },
  { value: 'naive', label: 'Naive' },
];

/**
 * The forecasting workspace.
 *
 * Three things are on screen together deliberately: the projection, the model's
 * *components*, and how the model scored when it was made to predict data it had
 * not seen. A forecast shown without those is a line a reader has no way to
 * weigh — which is precisely what both predecessors shipped, one of them with
 * random jitter added to make a second line look different.
 */
export function Forecasting({ dataset, colors }: { dataset: Dataset; colors: VizColors }) {
  const dates = dataset.columns.filter((c) => c.type === 'temporal');
  const measures = dataset.columns.filter((c) => c.type === 'numeric' && !c.isIdentifier);

  const [timeColumn, setTimeColumn] = useState(() => dates[0]?.name ?? '');
  const [measure, setMeasure] = useState(() => measures[0]?.name ?? '');
  const [aggregation, setAggregation] = useState<Aggregation>('sum');
  const [period, setPeriod] = useState<CalendarPeriod>(() => suggestPeriod(dataset, dates[0]));
  const [horizon, setHorizon] = useState(12);
  const [model, setModel] = useState<ForecastModelName | 'auto'>('auto');
  const [seasonality, setSeasonality] = useState(true);
  const [showComponents, setShowComponents] = useState(false);

  const [projection, setProjection] = useState<Projection | null>(null);
  const [comparison, setComparison] = useState<ModelComparison | null>(null);
  const [anomalies, setAnomalies] = useState<Anomaly[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const series = useCallback(
    () => ({
      timeColumn,
      ...(aggregation === 'count' ? {} : { measure }),
      aggregation,
      period,
    }),
    [timeColumn, measure, aggregation, period],
  );

  useEffect(() => {
    if (timeColumn === '') return;

    let cancelled = false;
    setBusy(true);
    setFailure(null);

    api
      .forecast(dataset.id, {
        ...series(),
        horizon,
        seasonality,
        ...(model === 'auto' ? {} : { model }),
      })
      .then((response) => {
        if (!cancelled) setProjection(response.forecast);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A refusal to forecast is an answer about the data, not a crash — and
        // the stale projection must go, or it reads as the answer.
        setProjection(null);
        setFailure(asFailure(error));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dataset.id, series, horizon, model, seasonality, timeColumn]);

  async function evaluate(): Promise<void> {
    setEvaluating(true);
    setFailure(null);
    try {
      const [back, anomalyResponse] = await Promise.all([
        api.backtest(dataset.id, { ...series(), horizon: Math.min(horizon, 30), folds: 4 }),
        api.anomalies(dataset.id, { ...series(), sensitivity: 1 }),
      ]);
      setComparison(back.comparison);
      setAnomalies(anomalyResponse.anomalies);
    } catch (error) {
      setFailure(asFailure(error));
    } finally {
      setEvaluating(false);
    }
  }

  if (dates.length === 0) {
    return (
      <div className="page">
        <div className="card empty">
          <h3>No date column in this dataset</h3>
          <p>
            Forecasting needs one. If your file has dates that were read as text, they will show as
            <span className="badge" style={{ margin: '0 4px' }}>
              categorical
            </span>
            on the Data tab — re-export them in ISO format (2024-03-01) and they will be recognised.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="intro">
        <h2>Forecast</h2>
        <p>
          A decomposable model: a trend that may bend at points the data justifies, plus any repeating
          cycle strong enough to measure. The band is a prediction interval — it widens with distance
          because the uncertainty does.
        </p>
      </header>

      <div className="controls">
        <label className="field">
          Date column
          <select value={timeColumn} onChange={(e) => setTimeColumn(e.target.value)}>
            {dates.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          Aggregation
          <select value={aggregation} onChange={(e) => setAggregation(e.target.value as Aggregation)}>
            <option value="sum">sum</option>
            <option value="avg">average</option>
            <option value="count">row count</option>
            <option value="median">median</option>
            <option value="max">max</option>
          </select>
        </label>

        {aggregation !== 'count' && (
          <label className="field">
            Measure
            <select value={measure} onChange={(e) => setMeasure(e.target.value)}>
              {measures.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="field">
          Bucket
          <select value={period} onChange={(e) => setPeriod(e.target.value as CalendarPeriod)}>
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          Horizon
          <select value={horizon} onChange={(e) => setHorizon(Number(e.target.value))}>
            {[6, 12, 24, 52].map((h) => (
              <option key={h} value={h}>
                {h} {period}s
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          Model
          <select
            value={model}
            onChange={(e) => setModel(e.target.value as ForecastModelName | 'auto')}
          >
            {MODELS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        <label className="check" style={{ alignSelf: 'flex-end', paddingBottom: 6 }}>
          <input
            type="checkbox"
            checked={seasonality}
            onChange={(e) => setSeasonality(e.target.checked)}
          />
          Seasonality
        </label>
      </div>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {projection !== null && (
        <>
          <section className="card">
            <header>
              <h2>
                {aggregation === 'count' ? 'Row count' : measure} — next {horizon} {period}s
              </h2>
              <span className="sub">
                {label(projection.model)} · R² {projection.diagnostics.r2.toFixed(3)}
                {projection.diagnostics.mape === null
                  ? ''
                  : ` · in-sample error ${projection.diagnostics.mape.toFixed(1)}%`}
              </span>
            </header>

            {busy && <p className="muted">Refitting…</p>}

            <ForecastChart
              forecast={projection}
              measureLabel={aggregation === 'count' ? 'rows' : measure}
              colors={colors}
              height={330}
            />

            <div className="row" style={{ marginTop: 12 }}>
              {projection.seasonalities.map((s) => (
                <span className="badge accent" key={s.name}>
                  {s.name}
                  {s.strength === null ? '' : ` ${Math.round(s.strength * 100)}%`}
                </span>
              ))}
              {projection.changepoints.length > 0 && (
                <span className="badge">
                  {projection.changepoints.length} trend change
                  {projection.changepoints.length === 1 ? '' : 's'}
                </span>
              )}
              <span className="badge">
                {Math.round(projection.confidenceLevel * 100)}% interval
              </span>
              {projection.history.length > 0 && (
                <span className="badge">{projection.history.length} points fitted</span>
              )}
              <button
                className="ghost tiny"
                onClick={() => setShowComponents(!showComponents)}
                style={{ marginLeft: 'auto' }}
              >
                {showComponents ? 'Hide components' : 'Show components'}
              </button>
            </div>

            {/* The model's own explanation of itself: what it detected, what it
                is extrapolating, and where it does not trust its own fit. */}
            {projection.notes.length > 0 && (
              <ul style={{ margin: '12px 0 0', paddingLeft: 18 }}>
                {projection.notes.map((note, index) => (
                  <li key={index} className="muted" style={{ fontSize: 13 }}>
                    {note}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card">
            <header>
              <h2>Fit &amp; residuals</h2>
              <span className="sub">where the model over- and under-shoots</span>
            </header>

            <div className="stat-strip">
              <div className="stat">
                <span className="stat-label">R²</span>
                <span className="stat-value">{projection.diagnostics.r2.toFixed(3)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">MAE</span>
                <span className="stat-value">{format(projection.diagnostics.mae)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">RMSE</span>
                <span className="stat-value">{format(projection.diagnostics.rmse)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">MAPE</span>
                <span className="stat-value">
                  {projection.diagnostics.mape === null ? '—' : `${projection.diagnostics.mape.toFixed(1)}%`}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Residual σ</span>
                <span className="stat-value">{format(projection.diagnostics.residualStdError)}</span>
              </div>
            </div>

            <ResidualChart forecast={projection} colors={colors} />
            <p className="muted" style={{ marginTop: 8 }}>
              Bars above zero are periods the model under-predicted; below zero, over-predicted. A healthy
              fit looks like noise around zero — a long run on one side means the model is missing a
              pattern.
            </p>
          </section>

          <section className="card">
            <header>
              <h2>Forecast values</h2>
              <span className="sub">the numbers behind the line</span>
            </header>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th className="num">Forecast</th>
                    <th className="num">Low</th>
                    <th className="num">High</th>
                  </tr>
                </thead>
                <tbody>
                  {projection.points.map((point) => (
                    <tr key={point.at}>
                      <td>{point.at.slice(0, 10)}</td>
                      <td className="num strong">{format(point.value)}</td>
                      <td className="num">{format(point.lower)}</td>
                      <td className="num">{format(point.upper)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted" style={{ marginTop: 8 }}>
              The {Math.round(projection.confidenceLevel * 100)}% interval widens with distance because
              uncertainty compounds over time.
            </p>
          </section>

          {showComponents && (
            <section className="card">
              <header>
                <h2>Components</h2>
                <span className="sub">the fit, taken apart</span>
              </header>
              <ComponentsChart projection={projection} colors={colors} />
              <p className="muted" style={{ marginTop: 8 }}>
                Trend is the underlying level; each seasonal line is the repeating deviation from it.
                Added together with the residual, they reconstruct the actual series exactly.
              </p>
            </section>
          )}

          {projection.changepoints.length > 0 && (
            <section className="card">
              <header>
                <h2>Trend changes</h2>
                <span className="sub">per-{period} slope, in {aggregation === 'count' ? 'rows' : measure}</span>
              </header>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="num">Slope before</th>
                      <th className="num">Slope after</th>
                      <th className="num">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projection.changepoints.map((point) => (
                      <tr key={point.at}>
                        <td>{point.at.slice(0, 10)}</td>
                        <td className="num">{point.slopeBefore.toFixed(2)}</td>
                        <td className="num">{point.slopeAfter.toFixed(2)}</td>
                        <td className="num">
                          <span className={`delta ${point.delta >= 0 ? 'up' : 'down'}`}>
                            {point.delta >= 0 ? '+' : ''}
                            {point.delta.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      <section className="card">
        <header>
          <h2>Is this forecast any good?</h2>
          <button className="primary" onClick={evaluate} disabled={evaluating}>
            {evaluating ? <span className="spinner" /> : 'Backtest against baselines'}
          </button>
        </header>

        {comparison === null ? (
          <p className="muted">
            Refits the model at several points in the past and scores it on what came next — then does
            the same for simpler baselines. In-sample R² always improves with complexity, so it cannot
            tell you this.
          </p>
        ) : (
          <>
            <Notice
              tone={comparison.best === 'decomposition' ? 'good' : 'warning'}
              message={`Best out-of-sample: ${label(comparison.best)}`}
              remedy={comparison.reason}
            />

            <BacktestChart comparison={comparison} colors={colors} />

            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>Model</th>
                    <th className="num">MAE</th>
                    <th className="num">RMSE</th>
                    <th className="num">MAPE</th>
                    <th className="num">Interval coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.evaluated.map((entry) => (
                    <tr key={entry.model} className={entry.model === comparison.best ? 'best' : undefined}>
                      <td>{label(entry.model)}</td>
                      <td className="num">{entry.mae.toFixed(2)}</td>
                      <td className="num">{entry.rmse.toFixed(2)}</td>
                      <td className="num">{entry.mape === null ? '—' : `${entry.mape.toFixed(1)}%`}</td>
                      <td className="num">
                        {/* Coverage well below the nominal level means the band
                            is too narrow to believe, whatever the MAE says. */}
                        <span
                          className={
                            entry.coverage < 0.7 ? 'delta down' : entry.coverage > 0.9 ? 'delta up' : ''
                          }
                        >
                          {Math.round(entry.coverage * 100)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted" style={{ marginTop: 8 }}>
              MAE and RMSE are in the measure's own units — lower is better. Coverage is the share of
              real values that fell inside the prediction interval; far below the nominal
              {' '}{Math.round((projection?.confidenceLevel ?? 0.95) * 100)}% means the band is optimistic.
            </p>
          </>
        )}
      </section>

      {anomalies !== null && (
        <section className="card">
          <header>
            <h2>Unexpected points</h2>
            <span className="sub">
              {anomalies.length} outside the model's expectation
            </span>
          </header>

          {anomalies.length === 0 ? (
            <p className="muted">
              Nothing unexplained. Every peak and trough is accounted for by the trend and the cycles
              the model found.
            </p>
          ) : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th />
                      <th className="num">Actual</th>
                      <th className="num">Expected</th>
                      <th className="num">Off by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anomalies.slice(0, 12).map((anomaly) => (
                      <tr key={anomaly.at}>
                        <td>{anomaly.at.slice(0, 10)}</td>
                        <td>
                          <span className={`badge ${anomaly.direction === 'above' ? 'good' : 'critical'}`}>
                            {anomaly.direction === 'above' ? '↑ high' : '↓ low'}
                          </span>
                        </td>
                        <td className="num">{format(anomaly.actual)}</td>
                        <td className="num">{format(anomaly.expected)}</td>
                        <td className="num">{anomaly.severity.toFixed(1)}σ</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="muted" style={{ marginTop: 8 }}>
                Measured against a fit that already accounts for trend and seasonality — so a regular
                weekly peak is not flagged, and the same size of jump on an ordinary day is.
              </p>
            </>
          )}
        </section>
      )}
    </div>
  );
}

function label(model: ForecastModelName): string {
  switch (model) {
    case 'decomposition':
      return 'Trend + seasonality';
    case 'seasonal-naive':
      return 'Seasonal naive';
    case 'linear':
      return 'Linear';
    case 'drift':
      return 'Drift';
    case 'naive':
      return 'Naive';
    case 'mean':
      return 'Mean';
  }
}

function suggestPeriod(dataset: Dataset, column: Column | undefined): CalendarPeriod {
  if (column === undefined) return 'month';
  // Rough heuristic from row count alone: the API picks a better one from the
  // actual span, and this only seeds the control.
  if (dataset.rowCount > 400) return 'day';
  if (dataset.rowCount > 100) return 'week';
  return 'month';
}

function format(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure('The request failed.', 'Try again.', 0);
}
